<template>
  <div v-click-outside="onClickOutside" class="LmEditor">
    <lm-label no-margin @click="focus">{{ label }}</lm-label>
    <lm-editor-menu-bar
      v-if="editor && showMenu"
      ref="EditorMenuBar"
      :editor="editor"
      class="sticky z-1 mt-2 mb-4"
      :class="modal ? '-top-6' : 'top-0'"
      @preventHide="preventHideMenuBar"
    />
    <editor-content
      :editor="editor"
      class="z-0 mt-2 pb-1 border-b"
      :class="{'border-error': error, 'border-gray-900': active}"
    />
    <span v-if="error" class="block w-full text-xs text-error pt-1">
      {{ errorMessage }}
    </span>
  </div>
</template>

<script>
import LmLabel from '@/components/ui/LmLabel'
import {Events} from '@/lib/definitions'
import EventHub from '@/lib/eventHub'
import {debounce} from 'lodash'
import LmEditorMenuBar from '@/components/form/LmEditorMenuBar'
import {Editor, EditorContent, Extension} from 'tiptap'
import {getMarkAttrs} from 'tiptap-utils'
import {
  Blockquote,
  Code,
  CodeBlock,
  Heading,
  OrderedList,
  BulletList,
  ListItem,
  Bold,
  Italic,
  // Link,
  Strike,
  HardBreak,
  History,
  Image,
  Mention
} from 'tiptap-extensions'
import Iframe from '@/components/form/editor/Iframe'
import Link from '@/components/form/editor/Link'
import {mapGetters} from 'vuex'

const PROFILE = 'profile'
const TOPIC = 'topic'
const COMMENT = 'comment'
export default {
  name: 'LmEditor',
  components: {
    LmLabel,
    LmEditorMenuBar,
    EditorContent
  },
  props: {
    value: {
      required: true,
      type: String
    },
    type: {
      validator(v) {
        return [PROFILE, COMMENT, TOPIC].indexOf(v) >= 0 ? v : false
      },
      default: TOPIC
    },
    optional: {
      type: Boolean,
      default: true
    },
    label: {
      type: String,
      default: 'Text'
    },
    modal: {
      type: Boolean, // editor is rendered inside modal
      default: true
    },
    useDirtyAux: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      error: false,
      errorMessage: '',
      editor: null,
      editorChange: false,
      active: false,
      showMenu: false,
      suggestionRange: null,
      navigatedUserIndex: 0,
      insertMention: () => {},
      updatedFromVModel: false,
      cancelClickOutside: false
    }
  },
  computed: {
    ...mapGetters(['currentLandscapeActiveUsers'])
  },
  watch: {
    value(val) {
      if (this.editor && !this.editorChange) {
        this.updatedFromVModel = true
        this.editor.setContent(val, true)
      }
      this.editorChange = false
    }
  },
  mounted() {
    const _self = this
    this.editor = new Editor({
      content: this.value,
      onUpdate: ({getHTML}) => this.onEditorChange(getHTML()),
      onFocus: () => {
        if (this.error) this.clearErrors()
        this.$nextTick(() => {
          this.active = true
          if (!this.showMenu) this.showMenu = true
        })
      },
      onBlur: () => {
        this.active = false
        setTimeout(() => {
          EventHub.emit(Events.CLOSE_FILTER_LIST)
        }, 200)
      },
      extensions: [
        new Blockquote(),
        new Code(),
        new CodeBlock(),
        new ListItem(),
        new BulletList(),
        new OrderedList(),
        new Bold(),
        new Italic(),
        new Link({onclick: this.onClickLink}),
        new Strike(),
        new HardBreak(),
        new History(),
        new Heading({levels: [1, 2]}),
        new Image(),
        new Iframe(),
        new (class extends Extension {
          keys() {
            return {
              'Mod-Enter': () => _self.$emit('submit'),
              'Mod-k': state => {
                const {from, to} = state.selection
                const selectedText = state.doc.textBetween(from, to, ' ')
                const attrs = getMarkAttrs(state, state.schema.marks.link)
                if (selectedText) {
                  _self.onClickLink(attrs.href, selectedText)
                }
                return true
              }
            }
          }
        })(),
        new Mention({
          // a list of all suggested items
          items: () => this.currentLandscapeActiveUsers,
          onFilter: (searchItems, query) => {
            if (!query) {
              return searchItems
            }
            return searchItems.filter(item =>
              item.username.toLowerCase().includes(query.toLowerCase())
            )
          },
          // is called when a suggestion starts
          onEnter: ({items, range, command, virtualNode}) => {
            EventHub.on(Events.SELECT_FILTER_LIST, this.selectUser)
            this.suggestionRange = range
            // we save the command for inserting a selected mention
            // this allows us to call it inside of our custom popup
            // via keyboard navigation and on click
            this.insertMention = command
            EventHub.emit(Events.OPEN_FILTER_LIST, {
              type: 'USER_MENTION',
              node: virtualNode,
              list: items
            })
          },
          // is called when a suggestion has changed
          onChange: ({items, range}) => {
            this.suggestionRange = range
            this.navigatedUserIndex = 0
            EventHub.emit(Events.UPDATE_FILTER_LIST, {
              list: items
            })
          },
          onExit: () => {
            EventHub.off(Events.SELECT_FILTER_LIST, this.selectUser)
            this.suggestionRange = null
            this.navigatedUserIndex = 0
            EventHub.emit(Events.CLOSE_FILTER_LIST)
          },
          onKeyDown: ({event}) => {
            // pressing up arrow
            if (event.keyCode === 38) {
              EventHub.emit(Events.PREV_SELECT_FILTER_LIST)
              return true
            }
            // pressing down arrow
            if (event.keyCode === 40) {
              EventHub.emit(Events.NEXT_SELECT_FILTER_LIST)
              return true
            }
            if (event.keyCode === 13) {
              EventHub.emit(Events.DO_SELECT_FILTER_LIST)
              return true
            }
            return false
          }
        })
      ]
    })
  },
  beforeDestroy() {
    this.editor.destroy()
  },
  methods: {
    onEditorChange(html) {
      this.editorChange = true
      if (html === '<p></p>') html = ''
      if (html !== '' && !this.updatedFromVModel) {
        this.$store.dispatch(this.useDirtyAux ? 'setDirtyAux' : 'setDirty')
      }
      if (this.updatedFromVModel) this.updatedFromVModel = false
      this.emitInput(html)
    },
    emitInput: debounce(function(html) {
      this.$emit('input', html)
    }, 200),
    validateData() {
      let value = this.editor.getHTML()
      if (this.value !== value) {
        // Not synced due to debounce
        this.$emit('input', value)
      }
      if (this.optional) return true
      if (value === '' || value === '<p></p>') {
        this.error = true
        this.errorMessage = `This field can't be blank`
        return false
      }
      return true
    },
    onClickLink(href, text) {
      this.$refs.EditorMenuBar.onClickLink(href, text)
    },
    onClickOutside() {
      this.$nextTick(() => {
        if (!this.cancelClickOutside) {
          this.showMenu = false
          this.editor.blur()
          EventHub.emit(Events.CLOSE_FILTER_LIST)
        }
        this.cancelClickOutside = false
      })
    },
    reset() {
      this.clearErrors()
    },
    clearErrors() {
      this.error = false
    },
    // we have to replace our suggestion text with a mention
    // so it's important to pass also the position of your suggestion text
    selectUser(user) {
      this.insertMention({
        range: this.suggestionRange,
        attrs: {
          id: user._id,
          label: user.username
        }
      })
      this.preventHideMenuBar()
    },
    preventHideMenuBar() {
      this.cancelClickOutside = true
      setTimeout(() => {
        this.cancelClickOutside = false
      }, 100)
    },
    focus() {
      setTimeout(() => {
        this.editor.focus()
      }, 0)
    },
    // P U B L I C
    addMention(user) {
      this.editor.commands.mention({
        id: user._id,
        label: user.username
      })
      this.focus()
    }
  }
}
</script>

<style>
.ProseMirror-focused {
  outline: none !important;
  border: none !important;
}
</style>
